import React, { useEffect, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, Modal, StyleSheet, FlatList, NativeScrollEvent, NativeSyntheticEvent, Platform } from 'react-native';

export interface TimeWheelPickerProps {
  visible: boolean;
  value?: string; // "HH:mm"
  onChange?: (v: string) => void;
  onConfirm?: () => void;
  onCancel?: () => void;
  mode?: '24h' | '12h';
  minuteStep?: number;
}

const pad = (n: number) => String(n).padStart(2, '0');

const ITEM_HEIGHT = 44;

const TimeWheelPicker: React.FC<TimeWheelPickerProps> = ({ visible, value = '09:00', onChange, onConfirm, onCancel, mode = '24h', minuteStep = 5 }) => {
  const [hour, setHour] = useState<number>(9);
  const [minute, setMinute] = useState<number>(0);

  const hours = Array.from({ length: 24 }).map((_, i) => i);
  const minutes = Array.from({ length: Math.ceil(60 / minuteStep) }).map((_, i) => i * minuteStep);

  const hourRef = useRef<FlatList<number> | null>(null);
  const minuteRef = useRef<FlatList<number> | null>(null);

  useEffect(() => {
    try {
      const parts = String(value || '09:00').split(':');
      const hh = parseInt(parts[0] || '9', 10);
      const mm = parseInt(parts[1] || '0', 10);
      const hhSafe = isNaN(hh) ? 9 : Math.max(0, Math.min(23, hh));
      const mmSafe = isNaN(mm) ? 0 : Math.max(0, Math.min(59, Math.round(mm / minuteStep) * minuteStep));
      setHour(hhSafe);
      setMinute(mmSafe);
      // scroll lists to position when opened
      setTimeout(() => {
        try { hourRef.current && (hourRef.current as any).scrollToIndex({ index: hhSafe, animated: false }); } catch (e) {}
        try { const mIndex = minutes.indexOf(mmSafe); minuteRef.current && (minuteRef.current as any).scrollToIndex({ index: mIndex >= 0 ? mIndex : 0, animated: false }); } catch (e) {}
      }, 50);
    } catch (e) {}
  }, [value, minuteStep]);

  const onHourScrollEnd = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const y = e.nativeEvent.contentOffset.y || 0;
    const idx = Math.round(y / ITEM_HEIGHT);
    const clamped = Math.max(0, Math.min(hours.length - 1, idx));
    if (clamped !== hour) setHour(clamped);
    // snap to exact position (some platforms/devices may not land exactly)
    try { (hourRef.current as any)?.scrollToOffset?.({ offset: clamped * ITEM_HEIGHT, animated: true }); } catch (e) {}
  };

  const onMinuteScrollEnd = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const y = e.nativeEvent.contentOffset.y || 0;
    const idx = Math.round(y / ITEM_HEIGHT);
    const clamped = Math.max(0, Math.min(minutes.length - 1, idx));
    const m = minutes[clamped] ?? 0;
    if (m !== minute) setMinute(m);
    try { (minuteRef.current as any)?.scrollToOffset?.({ offset: clamped * ITEM_HEIGHT, animated: true }); } catch (e) {}
  };

  // Called when user finishes dragging but momentum may not fire on some platforms
  const onHourDragEnd = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const y = e.nativeEvent.contentOffset.y || 0;
    const idx = Math.round(y / ITEM_HEIGHT);
    const clamped = Math.max(0, Math.min(hours.length - 1, idx));
    if (clamped !== hour) setHour(clamped);
    try { (hourRef.current as any)?.scrollToOffset?.({ offset: clamped * ITEM_HEIGHT, animated: true }); } catch (e) {}
  };

  const onMinuteDragEnd = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const y = e.nativeEvent.contentOffset.y || 0;
    const idx = Math.round(y / ITEM_HEIGHT);
    const clamped = Math.max(0, Math.min(minutes.length - 1, idx));
    const m = minutes[clamped] ?? 0;
    if (m !== minute) setMinute(m);
    try { (minuteRef.current as any)?.scrollToOffset?.({ offset: clamped * ITEM_HEIGHT, animated: true }); } catch (e) {}
  };

  useEffect(() => {
    try { onChange && onChange(`${pad(hour)}:${pad(minute)}`); } catch (e) {}
  }, [hour, minute]);

  const handleConfirm = () => {
    const v = `${pad(hour)}:${pad(minute)}`;
    try { onChange && onChange(v); } catch (e) {}
    try { onConfirm && onConfirm(); } catch (e) {}
  };

  const renderHour = ({ item }: { item: number }) => (
    <TouchableOpacity activeOpacity={0.7} onPress={() => {
      const idx = item;
      setHour(idx);
      try { (hourRef.current as any)?.scrollToOffset?.({ offset: idx * ITEM_HEIGHT, animated: true }); } catch (e) {}
    }}>
      <View style={[localStyles.item, item === hour && localStyles.itemActive]}>
        <Text style={[localStyles.itemText, item === hour && localStyles.itemTextActive]}>{pad(item)}</Text>
      </View>
    </TouchableOpacity>
  );

  const renderMinute = ({ item }: { item: number }) => (
    <TouchableOpacity activeOpacity={0.7} onPress={() => {
      const idx = minutes.indexOf(item);
      if (idx < 0) return;
      setMinute(item);
      try { (minuteRef.current as any)?.scrollToOffset?.({ offset: idx * ITEM_HEIGHT, animated: true }); } catch (e) {}
    }}>
      <View style={[localStyles.item, item === minute && localStyles.itemActive]}>
        <Text style={[localStyles.itemText, item === minute && localStyles.itemTextActive]}>{pad(item)}</Text>
      </View>
    </TouchableOpacity>
  );

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={localStyles.backdrop}>
        <View style={localStyles.container}>
          <Text style={localStyles.title}>选择时间</Text>
          <View style={localStyles.wheels}>
            <View style={{ position: 'relative', alignItems: 'center' }}>
              <FlatList
                ref={hourRef as any}
                data={hours}
                keyExtractor={(h) => String(h)}
                renderItem={renderHour}
                showsVerticalScrollIndicator={false}
                snapToInterval={ITEM_HEIGHT}
                decelerationRate={Platform.OS === 'ios' ? 'fast' : 0.98}
                onMomentumScrollEnd={onHourScrollEnd}
                onScrollEndDrag={onHourDragEnd}
                getItemLayout={(_, index) => ({ length: ITEM_HEIGHT, offset: ITEM_HEIGHT * index, index })}
                style={localStyles.column}
                contentContainerStyle={{ paddingVertical: ITEM_HEIGHT * 2 }}
              />
              <View style={localStyles.centerHighlight} />
              <Text style={{ marginTop: 12, fontSize: 14, fontWeight: '600', color: '#9CA3AF' }}>时</Text>
            </View>

            <View style={{ position: 'relative', alignItems: 'center' }}>
              <FlatList
                ref={minuteRef as any}
                data={minutes}
                keyExtractor={(m) => String(m)}
                renderItem={renderMinute}
                showsVerticalScrollIndicator={false}
                snapToInterval={ITEM_HEIGHT}
                decelerationRate={Platform.OS === 'ios' ? 'fast' : 0.98}
                onMomentumScrollEnd={onMinuteScrollEnd}
                onScrollEndDrag={onMinuteDragEnd}
                getItemLayout={(_, index) => ({ length: ITEM_HEIGHT, offset: ITEM_HEIGHT * index, index })}
                style={localStyles.column}
                contentContainerStyle={{ paddingVertical: ITEM_HEIGHT * 2 }}
              />
              <View style={localStyles.centerHighlight} />
              <Text style={{ marginTop: 12, fontSize: 14, fontWeight: '600', color: '#9CA3AF' }}>分</Text>
            </View>
          </View>
          <View style={localStyles.actions}>
            <TouchableOpacity onPress={() => { try { onCancel && onCancel(); } catch (e) {} }} style={localStyles.actionButton}><Text style={{ color: '#6B7280', fontWeight: '600', fontSize: 16 }}>取消</Text></TouchableOpacity>
            <TouchableOpacity onPress={handleConfirm} style={[localStyles.actionButton, localStyles.confirmButton]}><Text style={{ color: '#fff', fontWeight: '700', fontSize: 16 }}>确认</Text></TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const localStyles = StyleSheet.create({
  backdrop: { 
    flex: 1, 
    backgroundColor: 'rgba(0,0,0,0.5)', 
    justifyContent: 'center', 
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  container: { 
    width: '100%',
    maxWidth: 350,
    backgroundColor: '#ffffff', 
    borderRadius: 24, 
    padding: 24,
    paddingBottom: 28,
    alignItems: 'center',
    // react-native-web deprecates shadow* props — use boxShadow on web and keep native props elsewhere
    ...Platform.select({
      web: {
        boxShadow: '0px 8px 24px rgba(0,0,0,0.2)'
      },
      default: {
        shadowColor: '#000',
        shadowOpacity: 0.2,
        shadowOffset: { width: 0, height: 8 },
        shadowRadius: 24,
        elevation: 12,
      },
    }),
  },
  title: { 
    fontSize: 18, 
    fontWeight: '700', 
    marginBottom: 24,
    color: '#111827',
    letterSpacing: 0.5,
  },
  wheels: { 
    flexDirection: 'row', 
    width: '100%', 
    justifyContent: 'center',
    gap: 20,
    marginBottom: 24,
  },
  column: { 
    width: 100, 
    maxHeight: ITEM_HEIGHT * 5,
    borderRadius: 16,
    backgroundColor: 'rgba(243, 244, 246, 0.5)',
    overflow: 'hidden',
  },
  item: { 
    height: ITEM_HEIGHT, 
    alignItems: 'center', 
    justifyContent: 'center',
    borderRadius: 12,
  },
  itemActive: { 
    backgroundColor: 'rgba(255, 122, 0, 0.1)',
  },
  itemText: { 
    fontSize: 20, 
    color: '#9CA3AF',
    fontWeight: '500',
    letterSpacing: 1,
  },
  itemTextActive: { 
    fontWeight: '800',
    fontSize: 28,
    color: '#FF7A00',
  },
  actions: { 
    flexDirection: 'row', 
    width: '100%', 
    justifyContent: 'space-between',
    gap: 12,
  },
  actionButton: { 
    flex: 1,
    paddingVertical: 14, 
    paddingHorizontal: 20, 
    borderRadius: 12, 
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  confirmButton: { 
    backgroundColor: '#FF7A00',
    borderColor: '#FF7A00',
  },
  centerHighlight: { 
    position: 'absolute', 
    top: ITEM_HEIGHT * 2, 
    left: -8,
    right: -8,
    height: ITEM_HEIGHT, 
    borderRadius: 16, 
    backgroundColor: 'rgba(255, 122, 0, 0.08)',
    borderWidth: 2,
    borderColor: 'rgba(255, 122, 0, 0.2)',
    justifyContent: 'center', 
    alignItems: 'center',
    pointerEvents: 'none',
  },
});

export default TimeWheelPicker;
